import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { CopyNodeCommand } from '../../core/commands/CopyNodeCommand';
import { Button } from '../../components/ui/button';
import { Copy } from 'lucide-react';
import { useTableCellStore } from '../../stores/tableCellStore';
import { useDiagramStore } from '../../stores/diagramStore';
import { getTableCellRectByKey } from '../../domains/diagram/shapes/table/tableDrop.utils';
import { TableCellClipboard } from '../../domains/diagram/shapes/table/tableCellClipboard';

/**
 * Floating panel component for copy tool
 */
const CopyFloatingPanelComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const selectedCount = context.selection.size;
  
  const handleCopy = () => {
    onExecute();
  };

  return React.createElement(Button, {
    variant: 'outline',
    size: 'sm',
    onClick: handleCopy,
    className: 'h-8 gap-2',
    title: `Copy ${selectedCount} selected item${selectedCount > 1 ? 's' : ''}`
  }, [
    React.createElement(Copy, { key: 'icon', className: 'h-4 w-4' }),
    React.createElement('span', { key: 'text', className: 'text-xs' }, 'Copy')
  ]);
};

/**
 * Tool for copying selected items.
 * Uses proper command pattern for undo/redo support.
 */
export class CopyTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'copy',
    name: 'tools.copy',
    icon: 'copy',
    category: 'edit',
    description: 'Copy selected items',
    version: '1.0.0',
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: true,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    },
    
    shortcuts: [{
      key: 'c',
      modifiers: ['ctrl'],
      description: 'Copy selected items'
    }, {
      key: 'c',
      modifiers: ['cmd'],
      description: 'Copy selected items'
    }]
  };

  canExecute(context: IToolContext): boolean {
    return super.canExecute(context) && context.selection.size > 0;
  }

  execute(): ICommand | null {
    if (!this.context || this.context.selection.size === 0) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);
    const store = useDiagramStore.getState();
    const selectedNodes = store.nodes.filter((n) => selectedIds.includes(n.id)) as any[];
    const selectedTables = selectedNodes.filter((n) => n.type === 'table') as any[];
    if (selectedTables.length === 1) {
      const table = selectedTables[0];
      const sel = useTableCellStore.getState().selections[table.id];
      const key = sel?.anchorKey ?? sel?.selectedKeys?.[0];
      if (key) {
        const selectedKeys = Array.isArray(sel?.selectedKeys) ? sel?.selectedKeys : [];
        const normalizedKeys = selectedKeys
          .map((k) => getTableCellRectByKey(table, k as any)?.cellKey ?? k)
          .filter(Boolean)
          .map((k) => String(k));
        const uniqueKeys = Array.from(new Set(normalizedKeys));
        if (uniqueKeys.length > 1) {
          const parsed = uniqueKeys
            .map((k) => {
              const [rStr, cStr] = String(k).split(',');
              const r = Number(rStr);
              const c = Number(cStr);
              return Number.isFinite(r) && Number.isFinite(c) ? { key: k, r, c } : null;
            })
            .filter(Boolean) as Array<{ key: string; r: number; c: number }>;
          if (parsed.length > 0) {
            const minR = Math.min(...parsed.map((p) => p.r));
            const minC = Math.min(...parsed.map((p) => p.c));
            const attrs = (table.attrs ?? {}) as any;
            const items = parsed.map((p) => {
              const hit = getTableCellRectByKey(table, p.key as any);
              const cellRect = hit?.cellRect;
              const text = (attrs.cellText ?? {})[p.key];
              const style = (attrs.cellStyles ?? {})[p.key];
              const docked = store.nodes.filter((n: any) => {
                const dock = n?.attrs?.tableDock;
                return n?.type === 'sticky-note' && dock?.tableId === table.id && dock?.cellKey === p.key;
              });
              const dockedShapes = docked.map((n: any) => {
                const dock = n?.attrs?.tableDock;
                const off = dock?.offset;
                const offX =
                  typeof off?.x === 'number' && Number.isFinite(off.x) ? off.x : (cellRect ? (n.x - cellRect.x) : 0);
                const offY =
                  typeof off?.y === 'number' && Number.isFinite(off.y) ? off.y : (cellRect ? (n.y - cellRect.y) : 0);
                const baseAttrs = { ...(n?.attrs || {}) };
                delete (baseAttrs as any).tableDock;
                return {
                  type: n.type,
                  category: n.category,
                  w: n.w,
                  h: n.h,
                  width: n.width,
                  height: n.height,
                  text: n.text,
                  name: n.name,
                  fillColor: n.fillColor,
                  strokeColor: n.strokeColor,
                  strokeWidth: n.strokeWidth,
                  attrs: baseAttrs,
                  offset: { x: offX, y: offY },
                };
              });
              return { r: p.r - minR, c: p.c - minC, text, style, dockedShapes };
            });
            return {
              id: `copy-table-cell-range-${Date.now()}`,
              name: 'Copy Table Cell Range',
              timestamp: Date.now(),
              execute: async () => {
                TableCellClipboard.set({ version: 1, cells: { items } });
              },
              undo: async () => {},
              redo: async () => {},
            } as ICommand;
          }
        }
        const hit = getTableCellRectByKey(table, key as any);
        const cellKey = (hit?.cellKey ?? key) as any;
        const cellRect = hit?.cellRect;
        const attrs = (table.attrs ?? {}) as any;
        const text = (attrs.cellText ?? {})[cellKey];
        const style = (attrs.cellStyles ?? {})[cellKey];
        const docked = store.nodes.filter((n: any) => {
          const dock = n?.attrs?.tableDock;
          return n?.type === 'sticky-note' && dock?.tableId === table.id && dock?.cellKey === cellKey;
        });
        const dockedShapes = docked.map((n: any) => {
          const dock = n?.attrs?.tableDock;
          const off = dock?.offset;
          const offX =
            typeof off?.x === 'number' && Number.isFinite(off.x) ? off.x : (cellRect ? (n.x - cellRect.x) : 0);
          const offY =
            typeof off?.y === 'number' && Number.isFinite(off.y) ? off.y : (cellRect ? (n.y - cellRect.y) : 0);
          const baseAttrs = { ...(n?.attrs || {}) };
          delete (baseAttrs as any).tableDock;
          return {
            type: n.type,
            category: n.category,
            w: n.w,
            h: n.h,
            width: n.width,
            height: n.height,
            text: n.text,
            name: n.name,
            fillColor: n.fillColor,
            strokeColor: n.strokeColor,
            strokeWidth: n.strokeWidth,
            attrs: baseAttrs,
            offset: { x: offX, y: offY },
          };
        });
        return {
          id: `copy-table-cell-${Date.now()}`,
          name: 'Copy Table Cell Content',
          timestamp: Date.now(),
          execute: async () => {
            TableCellClipboard.set({ version: 1, text, style, dockedShapes });
          },
          undo: async () => {},
          redo: async () => {},
        } as ICommand;
      }
    }

    return new CopyNodeCommand(selectedIds, { x: 50, y: 50 });
  }

  getFloatingPanelComponent() {
    return CopyFloatingPanelComponent;
  }
}
